/* eslint-disable react-refresh/only-export-components */
import { useEffect, useRef } from 'react'
import { AlignJustify, Check, ChevronsLeft, List, LogIn, MoreVertical, PlugZap, Settings, Trash2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { openBasecampAuthWindow } from '@/lib/basecamp-api'
import { openWorkspaceExtensionSettings } from '@/lib/open-settings'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { useShellChrome } from './resizable-layout.flyout'

type SidebarPanelMenuProps = {
  openTabsVisible: boolean
  onOpenTabsVisibleChange: (visible: boolean) => void
  /** Rail is showing. Under auto-hide the collapse item pins or unpins. */
  collapsed?: boolean
  onCollapseSidebar: () => void
  onOpenTrash?: () => void
  dense?: boolean
  onDenseChange?: (dense: boolean) => void
  /** Basecamp limb actions. Layout items below stay on this same menu. */
  basecamp?: {
    connected: boolean
    authUrl?: string
    organizationId?: string
  }
}

/** Keeps the auto-hide flyout open while a mode menu is open. */
export function useShellMenuHold() {
  const chrome = useShellChrome()
  const menuOpenRef = useRef(false)
  const onMenuOpenChangeRef = useRef(chrome.onMenuOpenChange)
  onMenuOpenChangeRef.current = chrome.onMenuOpenChange
  useEffect(
    () => () => {
      if (menuOpenRef.current) onMenuOpenChangeRef.current(false)
    },
    [],
  )
  return (open: boolean) => {
    menuOpenRef.current = open
    chrome.onMenuOpenChange(open)
  }
}

/** Open Tabs, auto-hide, and pin. Same items on every mode menu. */
export function SidebarShellMenuItems({
  openTabsVisible,
  onOpenTabsVisibleChange,
  collapsed = false,
  onCollapseSidebar,
}: {
  openTabsVisible: boolean
  onOpenTabsVisibleChange: (visible: boolean) => void
  collapsed?: boolean
  onCollapseSidebar: () => void
}) {
  const chrome = useShellChrome()
  return (
    <>
      <DropdownMenuSeparator className="my-1.5" />
      <DropdownMenuItem
        className="cursor-pointer"
        onSelect={() => onOpenTabsVisibleChange(!openTabsVisible)}
        data-testid={testIds.leftSidebar.panelMenuOpenTabsToggle}
      >
        <span className="flex w-4 shrink-0 items-center justify-center mr-2">
          {openTabsVisible ? <Check className="h-3.5 w-3.5" /> : null}
        </span>
        {openTabsVisible ? 'Hide Open Tabs' : 'Show Open Tabs'}
      </DropdownMenuItem>
      <DropdownMenuItem
        className="cursor-pointer"
        onSelect={chrome.toggleAutoHide}
        data-testid={testIds.leftSidebar.panelMenuAutoHide}
      >
        <span className="flex w-4 shrink-0 items-center justify-center mr-2">
          {chrome.autoHide ? <Check className="h-3.5 w-3.5" /> : null}
        </span>
        Auto-hide sidebar
      </DropdownMenuItem>
      <DropdownMenuItem
        className="cursor-pointer"
        onSelect={onCollapseSidebar}
        data-testid={testIds.leftSidebar.panelMenuCollapse}
      >
        <ChevronsLeft className="mr-2 h-3.5 w-3.5" />
        {chrome.autoHide ? (collapsed ? 'Pin sidebar' : 'Unpin sidebar') : 'Collapse sidebar'}
      </DropdownMenuItem>
    </>
  )
}

/** Per-panel options menu (no workspace scope - see icon-bar scope button). */
export function SidebarPanelMenu({
  openTabsVisible,
  onOpenTabsVisibleChange,
  collapsed = false,
  onCollapseSidebar,
  onOpenTrash,
  dense,
  onDenseChange,
  basecamp,
}: SidebarPanelMenuProps) {
  const onMenuOpenChange = useShellMenuHold()
  return (
    <DropdownMenu onOpenChange={onMenuOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-6 shrink-0 items-center gap-1 rounded px-1',
            'text-sidebar-foreground/60 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
          )}
          title="Sidebar options"
          aria-label="Sidebar options"
          data-testid={testIds.leftSidebar.panelMenuTrigger}
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        data-testid={testIds.leftSidebar.panelMenu}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {basecamp ? (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => openWorkspaceExtensionSettings('basecamp', basecamp.organizationId)}
              data-testid={testIds.basecampSidebar.optionsSettings}
            >
              <Settings className="mr-2 h-3.5 w-3.5" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => openBasecampAuthWindow(basecamp.authUrl)}
              data-testid={testIds.basecampSidebar.optionsConnect}
            >
              {basecamp.connected ? (
                <PlugZap className="mr-2 h-3.5 w-3.5" />
              ) : (
                <LogIn className="mr-2 h-3.5 w-3.5" />
              )}
              {basecamp.connected ? 'Reconnect Basecamp' : 'Connect Basecamp'}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-1.5" />
          </>
        ) : null}

        {onDenseChange !== undefined ? (
          <>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => onDenseChange(false)}
              data-testid={testIds.leftSidebar.panelMenuDensity}
            >
              <AlignJustify className="mr-2 h-3.5 w-3.5" />
              Detailed layout
              {!dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={() => onDenseChange(true)}
            >
              <List className="mr-2 h-3.5 w-3.5" />
              Dense layout
              {dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-1.5" />
          </>
        ) : null}

        {onOpenTrash ? (
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={onOpenTrash}
            data-testid={testIds.leftSidebar.panelMenuTrash}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Trash
          </DropdownMenuItem>
        ) : null}

        <SidebarShellMenuItems
          openTabsVisible={openTabsVisible}
          onOpenTabsVisibleChange={onOpenTabsVisibleChange}
          collapsed={collapsed}
          onCollapseSidebar={onCollapseSidebar}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
